import { ERROR_LEVELS } from 'sentry-utils';
import { lazy } from 'react';

import {
    CHUNK_LOAD_FAILED,
    DEFAULT_RETRIES,
    LAZY_LOAD_ERROR,
    LOAD_DELAY_MS,
    MAX_RETRIES,
} from 'constants/lazyWithRetry';

import sentry from 'services/Sentry/SentryInstance';

import { isOnline } from 'helpers/environment';
import { isChunkError } from './helpers';

import { LoadComponentFunction, PreloadType } from './types';

const lazyLoad: LoadComponentFunction = ({
    importer,
    delayMs = LOAD_DELAY_MS,
    maxRetries = MAX_RETRIES,
    onError,
    preloadType = PreloadType.Init,
}) => {
    let retries = DEFAULT_RETRIES;

    const loadWithRetry: () => Promise<any> = async () => {
        try {
            if (!isOnline()) {
                // If the browser is offline, don't retry
                throw new Error('The browser is offline.');
            }

            return await importer();
        } catch (error) {
            const typedError = error as Error;

            const { hasError, isFailedToLoadChrome } = isChunkError(typedError);

            if (retries < maxRetries && hasError) {
                retries++;

                console.warn(`Error loading component: ${typedError?.message}. Retrying... (${retries}/${maxRetries})`);

                await new Promise((res) => setTimeout(res, delayMs));

                if (isFailedToLoadChrome) {
                    const url = new URL(
                        typedError?.message.replace('Failed to fetch dynamically imported module: ', '').trim()
                    );

                    // add a timestamp to the url to force a reload the module (and not use the cached version - cache busting)
                    url.searchParams.set('t', String(Number(new Date())));

                    try {
                        return await import(/* webpackChunkName: "[request]" */ url.href /* @vite-ignore */);
                    } catch (e) {
                        console.info('Retrying import');
                    }
                }

                return loadWithRetry();
            } else {
                console.error(`Error loading component: ${typedError?.message}. No more retries.`);

                throw error; // Reject the promise with the error
            }
        }
    };

    return lazy(
        () =>
            new Promise((resolve) => {
                setTimeout(
                    async () => {
                        try {
                            const module = await loadWithRetry();

                            resolve(module);
                        } catch (error) {
                            console.warn('Catch an error', error);

                            // onError action has to do smt, if it needs
                            onError && onError(error);

                            // @ts-ignore
                            sentry.logError(error, CHUNK_LOAD_FAILED, ERROR_LEVELS.CRITICAL, { ...error }, [
                                [LAZY_LOAD_ERROR, preloadType],
                            ]);
                        }
                    },
                    Boolean(retries) ? delayMs : DEFAULT_RETRIES
                );
            })
    );
};

export default lazyLoad;
